import camelCaseKeys from 'camelcase-keys';
import { HttpStatus } from 'constants/http-status';
import { EffectiveCartPayload } from 'hooks/effective-cart/useEffectiveCart';
import { PdAddress } from 'interfaces/address';
import { EffectiveCart } from 'interfaces/cart';
import { CustomerSubscriptionStatus } from 'interfaces/customer';
import {
  CartDiscountType,
  KlarnaSessionDetails,
  MollieSessionDetails,
  PaymentResponseType,
  PaymentSessionRequest,
} from 'modules/checkout/payment/types';
import { axios } from 'utils/axios';
import {
  ApplePaymentPaymentRequest,
  ExpressCheckout,
  ExpressCheckoutSummary,
} from '../interfaces/checkout';
import * as CheckoutModel from '../models/checkout';

interface CheckoutSessionBaseResponse {
  clientIp: string;
  landingPageUrl: string;
}

type AnonymousSession = CheckoutSessionBaseResponse;

export function isIdentifiedCustomer(data: any): data is IdentifiedCustomerSession {
  return 'customerId' in data;
}

export interface IdentifiedCustomerSession extends CheckoutSessionBaseResponse {
  createdAt: number;
  customerId: number;
  email: string;
  hasLoyaltyPoints: boolean;
  ordersCount: number;
  subscriptionStatus: CustomerSubscriptionStatus;
  firstName?: string;
  lastName?: string;
  phone?: string;
  postcode?: string;
  street?: string;
  city?: string;
  countryCode?: string;
}

interface TaxInfo {
  amount: string;
  rate: string;
  ratio?: string;
}

interface Address {
  country_code: string;
  post_code: string;
  first_name: string;
  last_name: string;
  city: string;
  address_1: string;
  phone: string | null;
  address_2: string | null;
  company: string | null;
}

interface ShippingInfo {
  amount: string;
  tax: TaxInfo[];
  method: string;
  price: string;
}

interface CartItem {
  id: string;
  name: string;
  image: string;
  variant_id: string;
  amount: string;
  product_id: string;
  tax: TaxInfo[];
  quantity: number;
  sku: string;
  price: string;
  price_variant: string | null;
  bundle: string | null;
  frozen: boolean;
  image_src: string;
  grams: string;
  variant_name: string;
  product_name: string;
  compare_at: string | null;
}

export const CheckoutStatus = {
  OPEN: 'open',
  CLOSED: 'closed',
  NOTIFIED: 'notified',
} as const;

export interface CheckoutResponse {
  billing_address?: Address;
  country_code: string;
  currency: string;
  customer_id: string;
  discount_origins: any[];
  payment: { mollie: MollieSessionDetails; klarna: KlarnaSessionDetails };
  discounts_amount?: string;
  discounts: any[];
  email: string;
  items: CartItem[];
  market_id: string;
  shipping_address: Address;
  shipping: ShippingInfo;
  status: (typeof CheckoutStatus)[keyof typeof CheckoutStatus];
  subtotal_amount: string;
  subtotal_tax: TaxInfo[];
  total_amount: string;
  total_tax: TaxInfo[];
}

interface PaymentPayload {
  orderId: string;
  params:
    | {
        mollie: {
          method: string;
          redirect_url: string;
          cancel_url: string;
          mandate_id?: string;
        };
      }
    | {
        klarna: {
          clientToken: string;
          paymentMethodCategories: {
            assetUrls: Record<string, string>;
            identifier: string;
            name: string;
          }[];
          sessionId: string;
        };
      };
}

interface CreatePaymentResponse {
  mollie: {
    checkout_url: string;
  };
}

export type CheckoutSessionResponse = AnonymousSession | IdentifiedCustomerSession;

/**
 * Create a session on backend and sets cookies related to session.
 * @returns CheckoutSessionResponse
 * @see https://petsdeli.atlassian.net/wiki/spaces/DOKUMENTAT/pages/1103331353/
 *
 * Due to BE bug, API might returns bad gate way error temporally.
 * The error toast is omitted as it's not critical for user.
 * @see https://petsdeli.atlassian.net/browse/PT-4078
 */
export const getCheckoutSession = (): Promise<CheckoutSessionResponse> =>
  axios
    .get<CheckoutSessionResponse>('/checkout/getSession', {
      params: { ref: window.location.href },
      errorHandling: {
        statusesToOmit: [HttpStatus.BAD_GATEWAY],
      },
    })
    .then((response) => response.data);

export const createExpressCheckout = ({
  code,
  checkoutId,
  ...effectiveCartPayload
}: {
  checkoutId: string;
  [effectiveCartPayloadKey: string]: any;
}): any =>
  axios
    .post('/payment/optile/express/preset', {
      listParams: {
        summaryUrl: `${location.protocol}//${location.host}/checkout/express`,
        cancelUrl: `${location.protocol}//${location.host}/cart`,
        returnUrl: `${location.protocol}//${location.host}/checkout/express`,
      },
      code,
      checkoutId,
      cart: effectiveCartPayload,
    })
    .then((response) => response.data);

export interface ExpressCheckoutInitInput {
  providerResponse: { [key: string]: string };
}

/**
 * Initialize Checkout for express checkout.
 * This API call creates checkout data on our backend and create a user if necessary
 */
export const expressCheckoutInit = ({
  providerResponse,
}: ExpressCheckoutInitInput): Promise<ExpressCheckout> =>
  axios
    .get(`/payment/optile/express/summary/${providerResponse.requestId}`)
    .then((response) => response.data)
    .then(CheckoutModel.expressCheckoutFromAmazonFormat);

interface ExpressCheckoutConfirmInput {
  cart: EffectiveCartPayload;
  subscribeNewsletter: boolean;
  captureUrl: string;
  audiences: Array<string>;
}
interface ExpressCheckoutOutput {
  redirect: string;
  status: boolean;
}

export const expressCheckoutConfirm = ({
  cart,
  captureUrl,
  subscribeNewsletter,
}: ExpressCheckoutConfirmInput): Promise<ExpressCheckoutOutput> =>
  axios
    .post(captureUrl, {
      subscribeNewsletter,
      cart,
    })
    .then((response) => response.data);

export const expressCheckoutCharge = ({ chargeUrl }: any) =>
  axios.post(chargeUrl, undefined).then(({ data }) => data);
export const expressCheckoutConfirmAuthentication = (confirmUrl: string, data: any) =>
  axios.post(confirmUrl, data);

export const expressCheckoutSummary = ({ summaryUrl }: any): Promise<ExpressCheckoutSummary> => {
  return axios
    .post(summaryUrl, undefined)
    .then(({ data }) => data)
    .then(CheckoutModel.expressCheckoutSummaryFromServerFormat);
};

/**
 * Creates payment session
 * @see https://petsdeli.atlassian.net/wiki/spaces/DOKUMENTAT/pages/312803348/
 */
export const createPaymentSession = (
  payload: PaymentSessionRequest
): Promise<PaymentResponseType> =>
  axios
    .post<PaymentResponseType>('/payment/optile/paymentSession', payload)
    .then((x) => camelCaseKeys(x.data, { deep: true }));
/**
 * Updates payment session
 * @see https://petsdeli.atlassian.net/wiki/spaces/DOKUMENTAT/pages/312803348/
 */
export const updatePaymentSession = (
  sessionId: string,
  payload: PaymentSessionRequest
): Promise<PaymentResponseType> =>
  axios
    .post<PaymentResponseType>(`/payment/optile/paymentSession/${sessionId}`, payload)
    .then((x) => camelCaseKeys(x.data, { deep: true }));

/** @TODO : Add detailed comment from Optile documentations */
export interface PaymentUrlParam {
  /** e.g 15819-27257 */
  shortId?: string;
  /** e.g 609269551dab52040cecef18u */
  customerRegistrationId?: string;
  /** e.g OK */
  interactionReason?: string;
  /** e.g 00000.STRIPE-PI.succeeded */
  resultCode?: string;
  /** e.g 62c2beaabef2dd0b8b410acfc */
  longId?: string;
  /** e.g 1656929955-jvl5dl56lbc0u */
  transactionId?: string;
  /** e.g PROCEED */
  interactionCode?: string;
  /** e.g 28.90 */
  amount?: string;
  /** e.g Pets+Deli+Tonius+GmbH */
  reference?: string;
  /** e.g EUR */
  currency?: string;
  /** e.g Mollie */
  paymentProvider?: string;
  /** e.g VISA */
  paymentMethod?: string;
  /** e.g 1656929955-jvl5dl56lbc0u */
  sessionId?: string;
}

interface SummaryResponse {
  effectiveCart: EffectiveCart;
  addresses: {
    shipping?: PdAddress;
    billing?: PdAddress;
  };
  payment?: {
    gateway: string;
    method: string;
  };
  customer: {
    email: string;
    id: number;
    isGuest: boolean;
    ordersCount: number;
    segments: string[];
  };
}

/**
 * Wrapper function to call actionUrl retrieved from url prams after payment redirection
 * This function flushes cart on backend
 */
export const finishPayment = (actionUrl: string, data?: PaymentUrlParam) =>
  axios.post(actionUrl, data).then((x) => x.data);

export const fetchPaymentSummary = (sessionId: string) =>
  axios
    .post<SummaryResponse>(`/payment/optile/paymentSession/express/${sessionId}/summary`)
    .then((x) => x.data);

export interface MolliePayload {
  method: string;
  redirectUrl: string;
  cancelUrl: string;
  mandateId?: string;
}

interface MollyCheckoutResponse {
  redirectUrl: string;
}

/**
 * Creates Mollie payment session.
 */
export const createMolliePayment = async (
  requestId: string,
  payload: MolliePayload
): Promise<MollyCheckoutResponse> =>
  axios.post(`/payment/mollie/payment/${requestId}`, payload).then(({ data }) => data);

interface ReauthenticatePaymentInput {
  method: string;
  customerId: number;
  mandateId?: string;
  redirectUrl?: string;
  cancelUrl?: string;
}

interface ReauthenticatePaymentResponse {
  redirectUrl: string;
}

/**
 * Creates Mollie payment session to reauthenticate payment method for customer that have old payment method as saved
 */
export const reauthenticatePayment = async ({
  redirectUrl = `${location.protocol}//${location.host}/account/subscriptions?success=1`,
  cancelUrl = `${location.protocol}//${location.host}/account/subscriptions`,
  ...others
}: ReauthenticatePaymentInput): Promise<ReauthenticatePaymentResponse> =>
  axios
    .post(`/payment/mollie/reauthentication/payment`, {
      redirectUrl,
      cancelUrl,
      ...others,
    })
    .then(({ data }) => data);

interface CheckReauthenticationRequiredInput {
  customerId: number;
}

export const PaymentMethodOption = {
  CreditCard: 'creditcard',
  PayPal: 'paypal',
} as const;

export type PaymentMethodOption = (typeof PaymentMethodOption)[keyof typeof PaymentMethodOption];

interface BasicResponse {
  id: string;
  mode: 'test' | 'production';
  method: PaymentMethodOption;
  resouce: 'mandate';
}

interface CreditCardResponse extends BasicResponse {
  method: 'creditcard';
  details: {
    cardHolder: string;
    cardNumber: string;
    cardLabel: string;
    cardFingerprint: string;
    cardExpiryDate: string;
  };
}
interface PaypalResponse extends BasicResponse {
  method: 'paypal';
  details: {
    consumerName: string;
    consumerAccount: string;
  };
}

export interface CheckReauthenticationRequiredResponse {
  reauth: boolean;
  mandates: Array<CreditCardResponse | PaypalResponse>;
}

/**
 * Checks if reauthentication of payment method is required.
 * If reauthentication is required and the customer has valid mandates, these are returned as well.
 */
export const checkReauthenticationRequired = async ({
  customerId,
}: CheckReauthenticationRequiredInput): Promise<CheckReauthenticationRequiredResponse> =>
  axios.get(`/payment/mollie/reauthentication/isRequired/${customerId}`).then(({ data }) => data);

export interface ApplePayMerchantSession {
  displayName: string;
  domainName: string;
  epochTimestamp: number;
  expiresAt: number;
  merchantIdentifier: string;
  merchantSessionIdentifier: string;
  nonce: string;
  operationalAnalyticsIdentifier: string;
  pspId: string;
  retries: number;
  signature: string;
}

interface ApplePayPaymentResponse {
  redirectUrl: string;
  errors?: { error: ApplePayError[]; type: string };
}

/**
 * Creates Apple Pay merchant session via Mollie
 */
export const createApplePayMerchatSession = (
  validationUrl: string,
  domain: string
): Promise<ApplePayMerchantSession> =>
  axios
    .post('/payment/mollie/applePay/session', {
      domain,
      validationUrl,
    })
    .then(({ data }) => data);

/**
 * Creates Apple Pay payment session via Mollie
 */
export const createApplePayPayment = ({
  payment,
}: {
  payment: ApplePaymentPaymentRequest;
}): Promise<ApplePayPaymentResponse> =>
  axios.post('/payment/mollie/applePay/payment', payment).then(({ data }) => data);

export interface CartRequest {
  customerId?: number;
  discount?: { type: CartDiscountType; code?: string };
  items: { variantId: number; quantity: number }[];
  shipping?: EffectiveCart['shipping'];
}

export interface PaypalExpressCreateOrderPayload {
  cart: CartRequest;
  checkoutId: string;
  method: 'paypal';
  listParams: {
    cancelUrl: string;
    summaryUrl: string;
    returnUrl: string;
  };
}

interface PaypalExpressCreateOrderResponse {
  requestId: string;
  paypalOrderId: string;
}

interface PaypalExpressAuthPaymentPayload {
  subscribeNewsletter?: boolean | undefined;
  cart: CartRequest;
}

interface PaypalExpressAuthPaymentResponse {
  actionReq?: 'charged';
  redirectUrl: string;
}

interface PaypalExpressInitPaymentResponse {
  redirectUrl: string;
}

/**
 * Creates Paypal Express order
 */
export const createPaypalExpressOrder = ({
  payload,
}: {
  payload: PaypalExpressCreateOrderPayload;
}): Promise<PaypalExpressCreateOrderResponse> =>
  axios
    .post('/payment/paypal/order', {
      ...payload,
    })
    .then(({ data }) => data);

/**
 * Initialize Paypal Express payment
 */
export const initPayPalExpressPayment = (
  requestId: string
): Promise<PaypalExpressInitPaymentResponse> =>
  axios.post(`/payment/paypal/init/${requestId}`).then(({ data }) => data);

/**
 * Authorize Paypal Express payment
 */
export const authPaymentPaypalExpress = (
  requestId: string,
  payload: PaypalExpressAuthPaymentPayload
): Promise<PaypalExpressAuthPaymentResponse> =>
  axios
    .post(`/payment/paypal/auth/${requestId}`, {
      ...payload,
    })
    .then(({ data }) => data);

interface PaypalExpressCapturePaymentResponse {
  redirect: string;
  status: boolean;
}

/**
 * Capture Paypal Express payment
 */
export const capturePaymentPaypalExpressPayment = (
  requestId: string | undefined
): Promise<PaypalExpressCapturePaymentResponse> =>
  axios.post(`/payment/paypal/capture/${requestId}`).then(({ data }) => data);

export interface ValidateShippingDetailsResponse {
  valid: boolean;
  missingFields?: [{ key: 'lastName'; type: 'text' }, { key: 'street'; type: 'text' }];
}

/**
 * Validates shipping details
 * Used in express checkout to validate shipping details
 */
export const validateShippingDetails = (
  requestId: string
): Promise<ValidateShippingDetailsResponse> =>
  axios.get(`/payment/validate-shipping-details/${requestId}`).then(({ data }) => data);

/**
 * Updates shipping details
 * Used in express checkout to update shipping details
 */
export const updateShippingDetails = ({
  requestId,
  payload,
}: {
  requestId: string;
  payload: {
    lastName?: string;
    street?: string;
  };
}): Promise<void> =>
  axios.post(`/payment/update-shipping-details/${requestId}`, payload).then(({ data }) => data);

/**
 * Fetches checkout data by orderId. Used to fetch checkout for a draft order.
 */
export const getCheckoutByOrderId = ({ orderId }: { orderId: string }): Promise<CheckoutResponse> =>
  axios
    .get(`/api/legacy/checkouts/${orderId}`, {
      isPdexApi: true,
    })
    .then(({ data }) => data.checkout);

/**
 * Creates payment for a PDEX checkout
 */
export const createPayment = async ({
  orderId,
  params,
}: PaymentPayload): Promise<CreatePaymentResponse> => {
  const { data } = await axios.post(`/api/legacy/checkouts/${orderId}/payment`, params, {
    isPdexApi: true,
  });

  return data.payment;
};
